import React, { useEffect, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { AuroraHeart } from "./AuroraHeart";
import { track } from "@/lib/analytics";

const REGISTER_URL = "https://app.withaurora.love/#/register";
const APPLE_STORE_URL = "https://apps.apple.com/us/app/aurora-ai-relationship-coach/id6765744036";
const GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=love.withaurora.app";
const DISMISS_KEY = "aurora_sticky_download_dismissed";

export const MobileStickyDownloadBar = () => {
  const { t } = useTranslation(["site", "landing"]);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const firedShownRef = useRef(false);

  const onScroll = useCallback(() => {
    if (typeof window === "undefined") return;
    // Show after the user has scrolled past roughly the hero (80vh)
    const threshold = window.innerHeight * 0.8;
    const nowVisible = window.scrollY > threshold;
    setVisible(nowVisible);
    if (nowVisible && !firedShownRef.current) {
      firedShownRef.current = true;
      track("sticky_bar_shown", { placement: "mobile_sticky" });
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const wasDismissed = window.sessionStorage?.getItem(DISMISS_KEY) === "1";
    if (wasDismissed) {
      setDismissed(true);
      return;
    }
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [onScroll]);

  if (dismissed) return null;

  const onDismiss = () => {
    track("sticky_bar_dismiss", { placement: "mobile_sticky" });
    setVisible(false);
    setDismissed(true);
    try {
      window.sessionStorage?.setItem(DISMISS_KEY, "1");
    } catch (e) {
      // ignore
    }
  };

  return (
    <div
      data-testid="mobile-sticky-download-bar"
      aria-hidden={!visible}
      className={`md:hidden fixed inset-x-0 bottom-0 z-40 pointer-events-none transition-all duration-500 ${
        visible ? "translate-y-0 opacity-100" : "translate-y-full opacity-0"
      }`}
    >
      <div
        className="mx-3 mb-3 rounded-2xl pointer-events-auto"
        style={{
          background:
            "linear-gradient(180deg, rgba(27, 10, 63, 0.97), rgba(18, 5, 46, 0.97))",
          border: "1px solid rgba(255,255,255,0.10)",
          boxShadow:
            "0 18px 40px rgba(0,0,0,0.45), 0 0 0 1px rgba(243,195,90,0.12) inset",
          backdropFilter: "blur(18px)",
          WebkitBackdropFilter: "blur(18px)",
        }}
      >
        <div className="px-3.5 py-3 flex items-center gap-3">
          <a
            href={REGISTER_URL}
            data-testid="sticky-bar-register"
            onClick={() => track("cta_register_click", { placement: "mobile_sticky" })}
            className="flex items-center gap-2.5 min-w-0 flex-1"
            aria-label={t("site:common.startFree", "Start Free Now")}
          >
            <AuroraHeart size={32} />
            <div className="min-w-0">
              <p className="text-[0.78rem] font-semibold text-white leading-tight truncate">
                {t("site:common.startFree", "Start Free Now")}
              </p>
              <p className="text-[0.66rem] text-white/55 leading-tight truncate">
                {t("site:home.freeBadge", "Free forever · Private by default")}
              </p>
            </div>
          </a>

          <a
            href={APPLE_STORE_URL}
            data-testid="sticky-bar-apple"
            onClick={() => track("cta_appstore_click", { placement: "mobile_sticky" })}
            className="shrink-0 size-11 rounded-xl border border-white/14 bg-black/70 flex items-center justify-center hover:border-white/35 active:scale-95 transition"
            aria-label="Download on the App Store"
          >
            <svg width="20" height="22" viewBox="0 0 22 26" fill="white" aria-hidden="true">
              <path d="M17.4 13.7c0-3.1 2.5-4.6 2.6-4.7-1.4-2-3.6-2.3-4.4-2.3-1.9-.2-3.6 1.1-4.6 1.1-.9 0-2.4-1.1-3.9-1-2 0-3.9 1.2-4.9 3-2.1 3.7-.5 9.1 1.5 12.1 1 1.5 2.2 3.1 3.7 3 1.5-.1 2.1-.9 3.9-.9 1.8 0 2.3.9 3.9.9 1.6 0 2.6-1.5 3.6-3 1.1-1.7 1.6-3.4 1.6-3.5-.1 0-3-1.2-3-4.7zM14.3 4.4c.8-1 1.4-2.4 1.2-3.8-1.2.1-2.6.8-3.4 1.8-.7.9-1.4 2.3-1.2 3.7 1.3.1 2.6-.7 3.4-1.7z" />
            </svg>
          </a>

          <a
            href={GOOGLE_PLAY_URL}
            data-testid="sticky-bar-google"
            onClick={() => track("cta_googleplay_click", { placement: "mobile_sticky" })}
            className="shrink-0 size-11 rounded-xl border border-white/14 bg-black/70 flex items-center justify-center hover:border-white/35 active:scale-95 transition"
            aria-label="Get it on Google Play"
          >
            <svg width="20" height="22" viewBox="0 0 24 26" aria-hidden="true">
              <defs>
                <linearGradient id="sb-gp1" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#00C3FF" />
                  <stop offset="100%" stopColor="#0078FF" />
                </linearGradient>
                <linearGradient id="sb-gp2" x1="0" y1="1" x2="1" y2="0">
                  <stop offset="0%" stopColor="#FFD600" />
                  <stop offset="100%" stopColor="#FFAA00" />
                </linearGradient>
                <linearGradient id="sb-gp3" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#FF3A44" />
                  <stop offset="100%" stopColor="#C71D38" />
                </linearGradient>
                <linearGradient id="sb-gp4" x1="1" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#00DA9C" />
                  <stop offset="100%" stopColor="#00A87A" />
                </linearGradient>
              </defs>
              <path d="M2 2 L14 13 L2 24 Z" fill="url(#sb-gp1)" />
              <path d="M14 13 L21 9 L21 17 Z" fill="url(#sb-gp2)" />
              <path d="M2 2 L14 13 L21 9 Z" fill="url(#sb-gp4)" />
              <path d="M2 24 L14 13 L21 17 Z" fill="url(#sb-gp3)" />
            </svg>
          </a>

          <button
            onClick={onDismiss}
            data-testid="sticky-bar-dismiss"
            aria-label={t("site:stickyBar.dismiss", "Dismiss download bar")}
            className="shrink-0 size-7 -mr-0.5 rounded-full text-white/40 hover:text-white/80 flex items-center justify-center"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M6 6l12 12M18 6l-6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      </div>
      {/* Safe-area bottom padding */}
      <div style={{ paddingBottom: "env(safe-area-inset-bottom)" }} />
    </div>
  );
};
